import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import apiService from '../services/apiService';

const MindmapContext = createContext(null);

export const useMindmap = () => {
  const context = useContext(MindmapContext);
  if (!context) {
    throw new Error('useMindmap must be used within MindmapProvider');
  }
  return context;
};

export const MindmapProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [mindmaps, setMindmaps] = useState([]);
  const [currentMindmap, setCurrentMindmap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load all mindmaps
  const loadMindmaps = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.fetchMindmaps();
      setMindmaps(data);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load mindmaps:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Load a single mindmap with nodes and edges
  const loadMindmap = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.fetchMindmap(id);
      setCurrentMindmap(data);
      return data;
    } catch (err) {
      setError(err.message);
      console.error('Failed to load mindmap:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new mindmap
  const createMindmap = useCallback(async (title, description) => {
    setLoading(true);
    setError(null);
    try {
      const mindmap = await apiService.createMindmap(title, description);
      setMindmaps(prev => [mindmap, ...prev]);
      return mindmap;
    } catch (err) {
      setError(err.message);
      console.error('Failed to create mindmap:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update mindmap metadata
  const updateMindmap = useCallback(async (id, updates) => {
    setError(null);
    try {
      const mindmap = await apiService.updateMindmap(id, updates);
      setMindmaps(prev => prev.map(m => m.id === id ? mindmap : m));
      if (currentMindmap?.mindmap?.id === id) {
        setCurrentMindmap(prev => ({ ...prev, mindmap }));
      }
      return mindmap;
    } catch (err) {
      setError(err.message);
      console.error('Failed to update mindmap:', err);
      return null;
    }
  }, [currentMindmap]);

  // Delete a mindmap
  const deleteMindmap = useCallback(async (id) => {
    setError(null);
    try {
      await apiService.deleteMindmap(id);
      setMindmaps(prev => prev.filter(m => m.id !== id));
      if (currentMindmap?.mindmap?.id === id) {
        setCurrentMindmap(null);
      }
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Failed to delete mindmap:', err);
      return false;
    }
  }, [currentMindmap]);

  // Create a new node
  const createNode = useCallback(async (mindmapId, nodeData) => {
    setError(null);
    try {
      const node = await apiService.createNode(mindmapId, nodeData);
      return node;
    } catch (err) {
      setError(err.message);
      console.error('Failed to create node:', err);
      return null;
    }
  }, []);

  // Update a node
  const updateNode = useCallback(async (nodeId, nodeData) => {
    setError(null);
    try {
      const node = await apiService.updateNode(nodeId, nodeData);
      return node;
    } catch (err) {
      setError(err.message);
      console.error('Failed to update node:', err);
      return null;
    }
  }, []);

  // Delete a node
  const deleteNode = useCallback(async (nodeId) => {
    setError(null);
    try {
      await apiService.deleteNode(nodeId);
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Failed to delete node:', err);
      return false;
    }
  }, []);

  // Batch update for offline sync
  const batchUpdate = useCallback(async (mindmapId, nodes, edges, mindmap) => {
    setError(null);
    try {
      await apiService.batchUpdate(mindmapId, nodes, edges, mindmap);
      if (currentMindmap?.mindmap?.id === mindmapId) {
        await loadMindmap(mindmapId);
      }
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Failed to batch update:', err);
      return false;
    }
  }, [currentMindmap, loadMindmap]);

  // Export mindmap as JSON
  const exportMindmap = useCallback(async (mindmapId) => {
    try {
      return await apiService.exportMindmap(mindmapId);
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, []);

  // Import mindmap from JSON
  const importMindmap = useCallback(async (jsonData) => {
    setLoading(true);
    setError(null);
    try {
      const mindmap = await apiService.importMindmap(jsonData);
      setMindmaps(prev => [mindmap, ...prev]);
      return mindmap;
    } catch (err) {
      setError(err.message);
      console.error('Failed to import mindmap:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear current mindmap
  const clearCurrentMindmap = useCallback(() => {
    setCurrentMindmap(null);
  }, []);

  const value = {
    mindmaps,
    currentMindmap,
    loading,
    error,
    loadMindmaps,
    loadMindmap,
    createMindmap,
    updateMindmap,
    deleteMindmap,
    createNode,
    updateNode,
    deleteNode,
    batchUpdate,
    exportMindmap,
    importMindmap,
    clearCurrentMindmap,
  };

  return (
    <MindmapContext.Provider value={value}>
      {children}
    </MindmapContext.Provider>
  );
};
